import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { nanoid } from 'nanoid';
import { v4 } from 'uuid';
import Actions from '@prism/actions';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _excluded = ["border", "height", "width", "className", "horizontal", "mediaWidth", "media", "mediaContent", "mediaGutter", "compact", "dark", "children", "title", "content"];
function useCard(props) {
  var _props$border = props.border,
      border = _props$border === void 0 ? true : _props$border,
      height = props.height,
      width = props.width,
      className = props.className,
      _props$horizontal = props.horizontal,
      horizontal = _props$horizontal === void 0 ? false : _props$horizontal,
      mediaWidth = props.mediaWidth,
      media = props.media,
      mediaContent = props.mediaContent;
      props.mediaGutter;
      var _props$compact = props.compact,
      compact = _props$compact === void 0 ? false : _props$compact,
      _props$dark = props.dark,
      dark = _props$dark === void 0 ? false : _props$dark,
      children = props.children,
      _props$title = props.title,
      title = _props$title === void 0 ? undefined : _props$title,
      _props$content = props.content,
      content = _props$content === void 0 ? undefined : _props$content,
      HTMLProps = _objectWithoutProperties(props, _excluded);
  /**
   * @remark - This allows us to pass single keys of the actions prop object without having to
   * strinctly null check each instance at runtime. We keep this as a memo to avoid
   * arbitrary callbacks leading to a render loop. - Darin Cassler 2021-09-26
   */


  var actions = React.useMemo(function () {
    return _objectSpread2({
      left: [],
      right: [],
      media: [],
      body: []
    }, props.actions);
  }, [props.actions]);
  /**
   * @remark - Compute some helper values to provide via our hook and that will be helpful
   * when creating our props in `elementProps` - Darin Cassler 2021-09-26
   */

  var flags = {
    hasMedia: !!media,
    hasBody: !!props.children || !!content || !!title,
    hasFooter: [].concat(_toConsumableArray(actions.left), _toConsumableArray(actions.right)).length > 0,
    mediaGutter: props.mediaGutter || props.gutter || 'none'
  };
  var padStyle = {
    padding: "".concat(compact ? '4px' : '8px')
  };
  /**
   * @remark - Here we are building out props to spread across our child components.
   * The goal is to consolidate these computed values into a single object that can be
   * accessed via our hook. - Darin Cassler 2021-09-26
   */

  var elementProps = {
    // Properties to spread across the parent element of a card including any HTML attributes
    card: _objectSpread2({
      className: classnames('card', className, {
        vertical: !horizontal,
        horizontal: !!horizontal,
        'has-media': !!media,
        compact: !!compact,
        'bg-dark': dark
      }),
      style: {
        borderWidth: border ? 1 : 0,
        borderColor: typeof border === 'string' ? border : undefined,
        height: height,
        width: width
      }
    }, HTMLProps),
    // Properties to spread across the Card Header element of a card.
    header: {
      className: classnames('card-header', {
        'text-white': dark
      }),
      style: padStyle
    },
    body: {
      className: classnames('card-body', {
        'text-white': dark
      }),
      style: padStyle,
      title: props.title,
      hasBody: flags.hasBody,
      gutter: props.gutter,
      actions: actions.body || []
    },
    // Properties to spread across the Card Layout element of a card.
    layout: {
      className: classnames('card-layout', {
        'text-white': dark,
        'layout-vertical': !horizontal,
        'layout-horizontal': !!horizontal
      })
    },
    // Properties to spread across the Card Footer element of a card
    footer: {
      className: classnames('card-footer', {
        'text-white': dark
      }),
      style: padStyle,
      hasFooter: flags.hasFooter,
      actionsLeft: actions.left || [],
      actionsRight: actions.right || []
    },
    media: {
      mediaIsBackground: false,
      src: media,
      hasMedia: !!media,
      mediaGutter: flags.mediaGutter,
      actions: actions.media || [],
      children: /*#__PURE__*/React.createElement(React.Fragment, null, mediaContent),
      className: 'card-media',
      style: {
        width: horizontal ? mediaWidth || '12rem' : 'auto'
      }
    }
  };
  /**
   * @remark - Introduce the NanoID library to Prism. This is a unique ID generator with an
   * import cost of 359 bytes gziped. - Darin Cassler 2021-09-26
   */

  var id = React.useRef(props.id || "prism-card-".concat(nanoid()));
  /**
   * @remark - Quickly determine our body content showing children first and then content.
   * Content can be any ReactNode or a string. Strings will be wrapped in a `<p>` tag.
   * - Darin Cassler 2021-09-26
   */

  var propContent = typeof props.content === 'string' ? /*#__PURE__*/React.createElement("p", null, props.content) : props.content;
  var innerContent = children || propContent;
  return _objectSpread2({
    elementProps: elementProps,
    flags: flags,
    id: id.current,
    innerContent: innerContent || null,
    mediaContent: mediaContent || null,
    HTMLProps: HTMLProps
  }, props);
}

var propTypes$4 = {
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.node)]),
  id: PropTypes.string,
  props: PropTypes.oneOfType([PropTypes.object])
};
var CardActions = function CardActions(_ref) {
  var _ref$actions = _ref.actions,
      actions = _ref$actions === void 0 ? null : _ref$actions,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? v4() : _ref$id,
      _ref$props = _ref.props,
      props = _ref$props === void 0 ? {} : _ref$props;

  if (!actions) {
    return null;
  }

  var actionsNode = null;

  if ( /*#__PURE__*/isValidElement(actions)) {
    actionsNode = actions;
  } else if (Array.isArray(actions)) {
    var actionsProps = _objectSpread2({
      menuAlignment: 'right'
    }, props);

    if ( /*#__PURE__*/isValidElement(actions[0])) {
      actionsNode = /*#__PURE__*/React.createElement(Actions, _extends({
        key: id
      }, actionsProps), actions);
    } else {
      actionsNode = /*#__PURE__*/React.createElement(Actions, _extends({
        key: id
      }, actionsProps, {
        actions: actions
      }));
    }
  }

  return actionsNode;
};
CardActions.propTypes = propTypes$4;
CardActions.displayName = 'CardActions';

/* eslint-disable react/jsx-filename-extension */
var CardHead = function CardHead(_ref) {
  var className = _ref.className,
      id = _ref.id,
      actions = _ref.actions,
      title = _ref.title;
  var _ref2 = [classnames('card-head', className), classnames('card-title', className), {
    actions: actions,
    id: id,
    props: {
      show: 1,
      iconOnly: true
    }
  }],
      parentClass = _ref2[0],
      titleClass = _ref2[1],
      actionProp = _ref2[2];
  return /*#__PURE__*/React.createElement("div", {
    className: parentClass,
    id: id
  }, title && /*#__PURE__*/React.createElement("h3", {
    className: titleClass
  }, title), actions && /*#__PURE__*/React.createElement(CardActions, actionProp));
};
CardHead.propTypes = {
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.node)]),
  id: PropTypes.string,
  title: PropTypes.string,
  className: PropTypes.string
};
CardHead.defaultProps = {
  actions: null,
  id: v4(),
  title: undefined,
  className: ''
};

/* eslint-disable react/jsx-filename-extension */
var propTypes$3 = {
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.node)]),
  children: PropTypes.node,
  gutter: PropTypes.oneOf([null, 'full', 'thin', 'none']),
  id: PropTypes.string,
  title: PropTypes.string,
  hasBody: PropTypes.bool
};
var CardBody = function CardBody(_ref) {
  var actions = _ref.actions,
      children = _ref.children,
      gutter = _ref.gutter,
      id = _ref.id,
      title = _ref.title,
      hasBody = _ref.hasBody;
  if (!hasBody) return null;
  var className = classnames('card-body', {
    'gutter-none': !gutter || gutter === 'none',
    'gutter-thin': gutter === 'thin',
    'gutter-full': gutter === 'full'
  });
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    id: id
  }, title || actions ? /*#__PURE__*/React.createElement(CardHead, {
    actions: actions,
    id: "".concat(id, "-head"),
    title: title
  }) : null, children);
};
CardBody.propTypes = propTypes$3;
CardBody.defaultProps = {
  actions: null,
  children: null,
  gutter: 'full',
  id: nanoid(),
  title: null,
  hasBody: true
};
CardBody.displayName = 'CardBody';

/* eslint-disable react/jsx-filename-extension */
var propTypes$2 = {
  actionsLeft: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.node)]),
  actionsRight: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.node)]),
  className: PropTypes.string,
  id: PropTypes.string,
  hasFooter: PropTypes.bool
};
var CardFooter = function CardFooter(_ref) {
  var actionsLeft = _ref.actionsLeft,
      actionsRight = _ref.actionsRight,
      hasFooter = _ref.hasFooter,
      id = _ref.id,
      propClassName = _ref.className;
  if (!hasFooter) return null;
  var className = classnames('card-footer', propClassName);
  var leftProps = {
    show: 2,
    textOnly: true
  };
  var rightProps = {
    show: 1,
    iconOnly: true
  };
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    id: id,
    role: "toolbar"
  }, actionsLeft ? /*#__PURE__*/React.createElement(CardActions, {
    actions: actionsLeft,
    id: "".concat(id, "-left-actions"),
    props: leftProps
  }) : null, actionsLeft || actionsRight ? /*#__PURE__*/React.createElement("span", {
    className: "card-footer-spacer"
  }) : null, actionsRight ? /*#__PURE__*/React.createElement(CardActions, {
    actions: actionsRight,
    id: "".concat(id, "-right-actions"),
    props: rightProps
  }) : null);
};
CardFooter.propTypes = propTypes$2;
CardFooter.displayName = 'CardFooter';
CardFooter.defaultProps = {
  actionsLeft: null,
  actionsRight: null,
  className: null,
  id: nanoid(),
  hasFooter: true
};

var propTypes$1 = {
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.node)]),
  children: PropTypes.node,
  className: PropTypes.string,
  gutter: PropTypes.oneOf([null, 'none', 'thin', 'full']),
  id: PropTypes.string,
  mediaIsBackground: PropTypes.bool,
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  style: PropTypes.oneOfType([PropTypes.object]),
  hasMedia: PropTypes.bool
};
var CardMedia = function CardMedia(_ref) {
  var actions = _ref.actions,
      children = _ref.children,
      hasMedia = _ref.hasMedia,
      propClassName = _ref.className,
      gutter = _ref.gutter,
      id = _ref.id,
      mediaIsBackground = _ref.mediaIsBackground,
      src = _ref.src,
      propStyle = _ref.style;
  if (!hasMedia) return null;
  var mediaNode = typeof src === 'string' ? /*#__PURE__*/React.createElement("img", {
    alt: "",
    src: src,
    style: {
      maxWidth: '100%'
    }
  }) : src;
  var backgroundImageSrc = typeof src === 'string' ? src : null;
  var className = classnames('card-media', propClassName, {
    'gutter-none': !gutter || gutter === 'none',
    'gutter-thin': gutter === 'thin',
    'gutter-full': gutter === 'full'
  });

  var style = _objectSpread2(_objectSpread2({}, propStyle), {}, {
    backgroundImage: mediaIsBackground && typeof src === 'string' && "url(".concat(backgroundImageSrc)
  });

  return /*#__PURE__*/React.createElement("div", {
    className: className,
    style: style,
    id: id
  }, mediaNode, actions ? /*#__PURE__*/React.createElement(CardActions, {
    actions: actions,
    id: "".concat(id, "-actions"),
    props: {
      show: 1,
      iconOnly: true
    }
  }) : null, children ? /*#__PURE__*/React.createElement("div", {
    className: "media-content"
  }, children) : null);
};
CardMedia.propTypes = propTypes$1;
CardMedia.displayName = 'CardMedia';
CardMedia.defaultProps = {
  actions: null,
  children: null,
  hasMedia: true,
  className: null,
  gutter: 'none',
  id: v4(),
  mediaIsBackground: false,
  src: null,
  style: null
};

/* eslint-disable react/jsx-filename-extension */
var propTypes = {
  children: PropTypes.node,
  horizontal: PropTypes.bool
};
var CardLayout = function CardLayout(_ref) {
  var children = _ref.children,
      horizontal = _ref.horizontal;
  var className = classnames('card-layout', {
    'layout-vertical': horizontal !== true,
    'layout-horizontal': horizontal === true
  });
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, children);
};
CardLayout.propTypes = propTypes;
CardLayout.defaultProps = {
  children: null,
  horizontal: false
};
CardLayout.displayName = 'CardLayout';

var Card$1 = function Card(props) {
  var _useCard = useCard(props),
      elementProps = _useCard.elementProps,
      mediaContent = _useCard.mediaContent,
      id = _useCard.id,
      innerContent = _useCard.innerContent;

  var card = elementProps.card,
      layout = elementProps.layout,
      media = elementProps.media,
      body = elementProps.body,
      footer = elementProps.footer;
  return /*#__PURE__*/React.createElement("div", _extends({}, card, {
    id: id
  }), /*#__PURE__*/React.createElement("div", layout, /*#__PURE__*/React.createElement(CardMedia, media, mediaContent), body.hasBody && /*#__PURE__*/React.createElement(CardBody, body, innerContent)), /*#__PURE__*/React.createElement(CardFooter, footer));
};
Card$1.propTypes = {
  /**
   * Insert left-aligned <Action /> in footer.
   */
  actions: PropTypes.shape({
    left: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.object])),
    right: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.object])),
    body: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.object])),
    media: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.object]))
  }),

  /**
   * Toggle a border around the card.
   */
  border: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * Optionally include your own CSS classes to style the card.
   */
  className: PropTypes.string,

  /**
   * URI of media asset to be used as `src` attribute OR a JSX element.
   */
  media: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Optionally include a caption beneath the media.
   */
  mediaContent: PropTypes.string,

  /**
   * Optionally set a fixed width of media - useful for horizontal layouts.
   */
  mediaWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Tweak gutter sizing for media. `full`, `thin`, `none`
   */
  mediaGutter: PropTypes.oneOf([null, 'full', 'thin', 'none']),

  /**
   * Tweak gutter sizing for the card. `full`, `thin`, `none`
   */
  gutter: PropTypes.oneOf([null, 'full', 'thin', 'none']),

  /**
   * Optionally, set an explicit height for the card. Applies as a CSS `height` property.
   */
  height: PropTypes.number,

  /**
   * Main presentational title for the card
   */
  title: PropTypes.string,

  /**
   * Optionally pass body content as a prop rather than children.
   */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Optionally, set an explicit width for the card. Applied as a CSS `width` property.
   */
  width: PropTypes.number,
  compact: PropTypes.bool,
  dark: PropTypes.bool
};
Card$1.defaultProps = {
  border: true,
  actions: undefined,
  compact: false,
  dark: false,
  gutter: 'full',
  className: '',
  mediaContent: undefined,
  mediaWidth: undefined,
  mediaGutter: 'none',
  media: undefined,
  height: undefined,
  title: undefined,
  content: undefined,
  width: undefined
};
Card$1.displayName = 'Card';

var Card = Object.assign(Card$1, {
  Actions: CardActions,
  Body: CardBody,
  Footer: CardFooter,
  Head: CardHead,
  Layout: CardLayout,
  Media: CardMedia
});

export { Card, CardActions, CardBody, CardFooter, CardHead, CardLayout, CardMedia, Card as default, useCard };
//# sourceMappingURL=index.js.map
